import { getSupabase } from './supabaseClient';
import type {
  KidsAgeGroup,
  KidsActivityDay,
  KidsAvatar,
  KidsAvatarInput,
  KidsBadge,
  KidsCmsAuditLog,
  KidsFamilySettings,
  KidsFeatureFlag,
  KidsFeatureFlagInput,
  KidsLearningArticle,
  KidsLearningModule,
  KidsLearningModuleInput,
  KidsLearningPath,
  KidsLevel,
  KidsLearningArticleDraftInput,
  KidsLearningPathInput,
  KidsMediaAsset,
  KidsMediaMetadataInput,
  KidsNotification,
  KidsChildAccessRequest,
  KidsChildAccessRequestInput,
  KidsParentRequestInput,
  KidsParentRequest,
  KidsProfile,
  KidsQuiz,
  KidsQuizAttempt,
  KidsQuizCategory,
  KidsQuizCategoryInput,
  KidsQuizDraftInput,
  KidsQuizAttemptResult,
  KidsQuizOption,
  KidsQuizQuestion,
  KidsQuizQuestionDraftInput,
  KidsRewardItem,
  KidsRewardItemInput,
  KidsLanguage,
  KidsBadgeInput,
  KidsLesson,
  KidsLessonInput,
  KidsLessonProgress,
} from '../types';

export type KidsCatalog = {
  categories: KidsQuizCategory[];
  featuredQuiz: KidsQuiz | null;
  questions: KidsQuizQuestion[];
  options: KidsQuizOption[];
  badges: KidsBadge[];
  rewardItems: KidsRewardItem[];
  featureFlags: KidsFeatureFlag[];
  levels: KidsLevel[];
  modules: KidsLearningModule[];
  lessons: KidsLesson[];
  learningPaths: KidsLearningPath[];
  articles: KidsLearningArticle[];
  avatars: KidsAvatar[];
  media: KidsMediaAsset[];
};

export type QuizWithQuestions = KidsQuiz & {
  questions: Array<KidsQuizQuestion & { options: KidsQuizOption[] }>;
};

export type ParentProgressSummary = {
  profiles: KidsProfile[];
  settings: KidsFamilySettings | null;
  attempts: KidsQuizAttempt[];
  activityDays: KidsActivityDay[];
  badges: Array<{ profile_id: string; badge_id: string; earned_at: string; badge?: KidsBadge | null }>;
  lessonProgress: Array<KidsLessonProgress & { lesson?: Pick<KidsLesson, 'title' | 'slug' | 'summary' | 'surah_id'> | null }>;
};

export type AdminQuizReportRow = {
  quiz_id: string;
  quiz_title: string;
  quiz_slug: string;
  attempts: number;
  average_accuracy: number;
  total_stars: number;
  total_xp: number;
  status: 'Healthy' | 'Review explanations' | 'Needs attempts';
};

export type AdminQuizReadinessRow = {
  quiz_id: string;
  quiz_title: string;
  quiz_slug: string;
  publish_status: KidsQuiz['status'];
  is_active: boolean;
  category_title: string;
  declared_questions: number;
  actual_questions: number;
  missing_questions: number;
  invalid_answer_questions: number;
  missing_explanations: number;
  status: 'Ready to publish' | 'Needs questions' | 'Fix answers' | 'Add explanations';
};

function throwIfError(error: { message: string } | null) {
  if (error) {
    throw new Error(error.message);
  }
}

export async function loadKidsCatalog(): Promise<KidsCatalog> {
  const client = getSupabase();

  const [
    categoriesResult,
    quizzesResult,
    badgesResult,
    rewardItemsResult,
    featureFlagsResult,
    levelsResult,
    modulesResult,
    lessonsResult,
    learningPathsResult,
    articlesResult,
    avatarsResult,
    mediaResult,
  ] = await Promise.all([
    client
      .from('kids_quiz_categories')
      .select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)')
      .eq('is_active', true)
      .order('sort_order'),
    client
      .from('kids_quizzes')
      .select('*, cover_media:kids_media_assets(id, path, filename, public_url, alt_text)')
      .eq('is_active', true)
      .eq('status', 'published')
      .order('created_at')
      .limit(1),
    client.from('kids_badges').select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)').eq('is_active', true).order('sort_order'),
    client
      .from('kids_reward_items')
      .select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)')
      .eq('is_active', true)
      .order('cost_stars'),
    client.from('kids_feature_flags').select('*').neq('status', 'hidden').order('launch_order'),
    client.from('kids_levels').select('*').order('level_number'),
    client.from('kids_learning_modules').select('*').order('sort_order'),
    client.from('kids_lessons').select('*').eq('is_active', true).order('sort_order').limit(8),
    client
      .from('kids_learning_paths')
      .select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)')
      .eq('is_active', true)
      .order('sort_order'),
    client
      .from('kids_learning_articles')
      .select('*, cover_media:kids_media_assets(id, path, filename, public_url, alt_text)')
      .eq('status', 'published')
      .order('sort_order')
      .limit(6),
    client.from('kids_avatars').select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)').eq('is_active', true).order('sort_order'),
    client.from('kids_media_assets').select('*').eq('status', 'active').order('created_at', { ascending: false }).limit(12),
  ]);

  [
    categoriesResult,
    quizzesResult,
    badgesResult,
    rewardItemsResult,
    featureFlagsResult,
    levelsResult,
    modulesResult,
    lessonsResult,
    learningPathsResult,
    articlesResult,
    avatarsResult,
    mediaResult,
  ].forEach((result) => throwIfError(result.error));

  const featuredQuiz = (quizzesResult.data?.[0] as KidsQuiz | undefined) ?? null;
  const questionResult = featuredQuiz
    ? await client.from('kids_quiz_questions').select('*').eq('quiz_id', featuredQuiz.id).eq('is_active', true).order('sort_order')
    : { data: [], error: null };
  throwIfError(questionResult.error);

  const questions = (questionResult.data ?? []) as KidsQuizQuestion[];
  const questionIds = questions.map((question) => question.id);
  const optionResult = questionIds.length
    ? await client.from('kids_quiz_options').select('*').in('question_id', questionIds).order('sort_order')
    : { data: [], error: null };
  throwIfError(optionResult.error);

  return {
    categories: (categoriesResult.data ?? []) as KidsQuizCategory[],
    featuredQuiz,
    questions,
    options: (optionResult.data ?? []) as KidsQuizOption[],
    badges: (badgesResult.data ?? []) as KidsBadge[],
    rewardItems: (rewardItemsResult.data ?? []) as KidsRewardItem[],
    featureFlags: (featureFlagsResult.data ?? []) as KidsFeatureFlag[],
    levels: (levelsResult.data ?? []) as KidsLevel[],
    modules: (modulesResult.data ?? []) as KidsLearningModule[],
    lessons: (lessonsResult.data ?? []) as KidsLesson[],
    learningPaths: (learningPathsResult.data ?? []) as KidsLearningPath[],
    articles: (articlesResult.data ?? []) as KidsLearningArticle[],
    avatars: (avatarsResult.data ?? []) as KidsAvatar[],
    media: (mediaResult.data ?? []) as KidsMediaAsset[],
  };
}

export async function listPublishedQuizzes(): Promise<KidsQuiz[]> {
  const { data, error } = await getSupabase()
    .from('kids_quizzes')
    .select('*, cover_media:kids_media_assets(id, path, filename, public_url, alt_text)')
    .eq('is_active', true)
    .eq('status', 'published')
    .order('created_at', { ascending: false });

  throwIfError(error);
  return (data ?? []) as KidsQuiz[];
}

export async function listActiveAvatars(): Promise<KidsAvatar[]> {
  const { data, error } = await getSupabase()
    .from('kids_avatars')
    .select('*, media:kids_media_assets(id, path, filename, public_url, alt_text)')
    .eq('is_active', true)
    .order('sort_order');

  throwIfError(error);
  return (data ?? []) as KidsAvatar[];
}

export async function loadQuizWithQuestions(quizSlug: string): Promise<QuizWithQuestions | null> {
  const client = getSupabase();
  const quizResult = await client
    .from('kids_quizzes')
    .select('*')
    .eq('slug', quizSlug)
    .eq('is_active', true)
    .eq('status', 'published')
    .maybeSingle();

  throwIfError(quizResult.error);
  if (!quizResult.data) return null;

  const questionResult = await client
    .from('kids_quiz_questions')
    .select('*')
    .eq('quiz_id', quizResult.data.id)
    .eq('is_active', true)
    .order('sort_order');
  throwIfError(questionResult.error);

  const questions = (questionResult.data ?? []) as KidsQuizQuestion[];
  const optionsResult = questions.length
    ? await client.from('kids_quiz_options').select('*').in(
        'question_id',
        questions.map((question) => question.id),
      )
    : { data: [], error: null };
  throwIfError(optionsResult.error);

  const options = (optionsResult.data ?? []) as KidsQuizOption[];
  return {
    ...(quizResult.data as KidsQuiz),
    questions: questions.map((question) => ({
      ...question,
      options: options
        .filter((option) => option.question_id === question.id)
        .sort((left, right) => left.sort_order - right.sort_order),
    })),
  };
}

export async function loadAdminQuizPreview(quizSlug?: string): Promise<QuizWithQuestions | null> {
  const client = getSupabase();
  let quizQuery = client
    .from('kids_quizzes')
    .select('*')
    .order('updated_at', { ascending: false })
    .limit(1);

  if (quizSlug) {
    quizQuery = client.from('kids_quizzes').select('*').eq('slug', quizSlug).limit(1);
  }

  const quizResult = await quizQuery.maybeSingle();
  throwIfError(quizResult.error);
  if (!quizResult.data) return null;

  const questionResult = await client
    .from('kids_quiz_questions')
    .select('*')
    .eq('quiz_id', quizResult.data.id)
    .order('sort_order');
  throwIfError(questionResult.error);

  const questions = (questionResult.data ?? []) as KidsQuizQuestion[];
  const optionsResult = questions.length
    ? await client
        .from('kids_quiz_options')
        .select('*')
        .in('question_id', questions.map((question) => question.id))
        .order('sort_order')
    : { data: [], error: null };
  throwIfError(optionsResult.error);

  const options = (optionsResult.data ?? []) as KidsQuizOption[];
  return {
    ...(quizResult.data as KidsQuiz),
    questions: questions.map((question) => ({
      ...question,
      options: options
        .filter((option) => option.question_id === question.id)
        .sort((left, right) => left.sort_order - right.sort_order),
    })),
  };
}

export async function completeQuizAttempt(input: {
  profileId: string;
  quizSlug: string;
  selectedOptionIds: string[];
}): Promise<KidsQuizAttemptResult> {
  const { data, error } = await getSupabase().rpc('complete_kids_quiz_attempt', {
    p_profile_id: input.profileId,
    p_quiz_slug: input.quizSlug,
    p_selected_option_ids: input.selectedOptionIds,
  });

  throwIfError(error);
  return data as KidsQuizAttemptResult;
}

export async function listChildProfiles(): Promise<KidsProfile[]> {
  const { data, error } = await getSupabase().from('kids_profiles').select('*').order('created_at');
  throwIfError(error);
  return (data ?? []) as KidsProfile[];
}

export async function loadParentProgressSummary(): Promise<ParentProgressSummary> {
  const client = getSupabase();

  const [
    profilesResult,
    settingsResult,
    attemptsResult,
    activityResult,
    badgesResult,
    lessonsResult,
  ] = await Promise.all([
    client.from('kids_profiles').select('*').order('created_at'),
    client.from('kids_family_settings').select('*').maybeSingle(),
    client
      .from('kids_quiz_attempts')
      .select('*, quiz:kids_quizzes(title, slug, difficulty)')
      .order('completed_at', { ascending: false, nullsFirst: false })
      .limit(20),
    client.from('kids_activity_days').select('*').order('activity_date', { ascending: false }).limit(30),
    client.from('kids_badge_awards').select('profile_id, badge_id, earned_at, badge:kids_badges(title, slug, description, icon_key, sort_order)').order('earned_at', { ascending: false }),
    client
      .from('kids_lesson_progress')
      .select('*, lesson:kids_lessons(title, slug, summary, surah_id)')
      .order('updated_at', { ascending: false })
      .limit(30),
  ]);

  [profilesResult, settingsResult, attemptsResult, activityResult, badgesResult, lessonsResult].forEach((result) => throwIfError(result.error));

  return {
    profiles: (profilesResult.data ?? []) as KidsProfile[],
    settings: (settingsResult.data as KidsFamilySettings | null) ?? null,
    attempts: (attemptsResult.data ?? []) as KidsQuizAttempt[],
    activityDays: (activityResult.data ?? []) as KidsActivityDay[],
    badges: ((badgesResult.data ?? []) as unknown) as ParentProgressSummary['badges'],
    lessonProgress: ((lessonsResult.data ?? []) as unknown) as ParentProgressSummary['lessonProgress'],
  };
}

export async function loadChildProgress(profileId: string) {
  const summary = await loadParentProgressSummary();
  const profile = summary.profiles.find((item) => item.id === profileId) ?? summary.profiles[0] ?? null;
  const targetProfileId = profile?.id ?? profileId;

  return {
    ...summary,
    profile,
    attempts: summary.attempts.filter((attempt) => attempt.profile_id === targetProfileId),
    activityDays: summary.activityDays.filter((day) => day.profile_id === targetProfileId),
    badges: summary.badges.filter((badge) => badge.profile_id === targetProfileId),
    lessonProgress: summary.lessonProgress.filter((lesson) => lesson.profile_id === targetProfileId),
  };
}

export async function listAgeGroups(): Promise<KidsAgeGroup[]> {
  const { data, error } = await getSupabase().from('kids_age_groups').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsAgeGroup[];
}

export async function saveFamilySettings(input: { language: KidsLanguage; daily_goal_minutes: number }) {
  const {
    data: { user },
    error: userError,
  } = await getSupabase().auth.getUser();
  throwIfError(userError);
  if (!user) {
    throw new Error('A parent account is required to save family settings.');
  }

  const { error } = await getSupabase()
    .from('kids_family_settings')
    .upsert({
      user_id: user.id,
      language: input.language,
      daily_goal_minutes: input.daily_goal_minutes,
    });

  throwIfError(error);
}

export async function createChildProfile(input: Pick<KidsProfile, 'display_name' | 'age_group_id' | 'avatar_key'>) {
  const {
    data: { user },
    error: userError,
  } = await getSupabase().auth.getUser();
  throwIfError(userError);
  if (!user) {
    throw new Error('A parent account is required to create a child profile.');
  }

  const { data, error } = await getSupabase()
    .from('kids_profiles')
    .insert({ ...input, user_id: user.id })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsProfile;
}

export async function selectLearningPath(profileId: string, learningPathId: string) {
  const { data, error } = await getSupabase()
    .from('kids_profile_learning_paths')
    .upsert({ profile_id: profileId, learning_path_id: learningPathId })
    .select('*')
    .single();

  throwIfError(error);
  return data;
}

export async function loadLessonBySlug(lessonSlug: string): Promise<KidsLesson | null> {
  const { data, error } = await getSupabase()
    .from('kids_lessons')
    .select('*')
    .eq('slug', lessonSlug)
    .eq('is_active', true)
    .maybeSingle();

  throwIfError(error);
  return (data as KidsLesson | null) ?? null;
}

export async function saveLessonProgress(input: {
  profile_id: string;
  lesson_id: string;
  status: KidsLessonProgress['status'];
  progress_percent: number;
}): Promise<KidsLessonProgress> {
  const timestamp = new Date().toISOString();
  const { data, error } = await getSupabase()
    .from('kids_lesson_progress')
    .upsert(
      {
        ...input,
        started_at: input.status === 'not_started' ? null : timestamp,
        completed_at: input.status === 'completed' ? timestamp : null,
      },
      { onConflict: 'profile_id,lesson_id' },
    )
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsLessonProgress;
}

export async function listChildNotifications(profileId: string): Promise<KidsNotification[]> {
  const { data, error } = await getSupabase()
    .from('kids_notifications')
    .select('*')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false })
    .limit(20);

  throwIfError(error);
  return (data ?? []) as KidsNotification[];
}

export async function markNotificationRead(notificationId: string): Promise<KidsNotification> {
  const { data, error } = await getSupabase()
    .from('kids_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', notificationId)
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsNotification;
}

export async function submitParentRequest(input: KidsParentRequestInput) {
  const { error } = await getSupabase().from('kids_parent_requests').insert(input);
  throwIfError(error);
}

export async function submitChildAccessRequest(input: KidsChildAccessRequestInput) {
  const { error } = await getSupabase().from('kids_child_access_requests').insert({
    ...input,
    child_email: input.child_email.trim().toLowerCase(),
    parent_email: input.parent_email.trim().toLowerCase(),
    message: input.message?.trim() || null,
  });
  throwIfError(error);
}

export async function listChildAccessRequests(): Promise<KidsChildAccessRequest[]> {
  const { data, error } = await getSupabase()
    .from('kids_child_access_requests')
    .select('*')
    .order('created_at', { ascending: false });

  throwIfError(error);
  return (data ?? []) as KidsChildAccessRequest[];
}

export async function approveChildAccessRequest(request: KidsChildAccessRequest): Promise<KidsChildAccessRequest> {
  const { data: currentRequest, error: currentRequestError } = await getSupabase()
    .from('kids_child_access_requests')
    .select('*')
    .eq('id', request.id)
    .single();

  throwIfError(currentRequestError);

  const latest = currentRequest as KidsChildAccessRequest;
  if (latest.status === 'approved' && latest.approved_profile_id) return latest;
  if (latest.status !== 'pending') {
    throw new Error('Only pending child access requests can be approved.');
  }

  const ageGroups = await listAgeGroups();
  const ageGroupId = ageGroups.find((ageGroup) => ageGroup.slug === latest.age_group_slug)?.id ?? ageGroups[0]?.id ?? null;
  const profile = await createChildProfile({
    display_name: latest.child_name,
    age_group_id: ageGroupId,
    avatar_key: 'emerald-explorer',
  });

  const { data, error } = await getSupabase()
    .from('kids_child_access_requests')
    .update({
      status: 'approved',
      approved_profile_id: profile.id,
    })
    .eq('id', request.id)
    .eq('status', 'pending')
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsChildAccessRequest;
}

export async function declineChildAccessRequest(requestId: string): Promise<KidsChildAccessRequest> {
  const { data, error } = await getSupabase()
    .from('kids_child_access_requests')
    .update({ status: 'declined' })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsChildAccessRequest;
}

export async function listMediaAssets(category?: KidsMediaAsset['category']): Promise<KidsMediaAsset[]> {
  let query = getSupabase().from('kids_media_assets').select('*').order('created_at', { ascending: false });
  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;
  throwIfError(error);
  return (data ?? []) as KidsMediaAsset[];
}

export async function listAdminCategories(): Promise<KidsQuizCategory[]> {
  const { data, error } = await getSupabase().from('kids_quiz_categories').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsQuizCategory[];
}

export async function saveQuizCategory(input: KidsQuizCategoryInput): Promise<KidsQuizCategory> {
  const { data, error } = await getSupabase()
    .from('kids_quiz_categories')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsQuizCategory;
}

export async function listAdminLearningPaths(): Promise<KidsLearningPath[]> {
  const { data, error } = await getSupabase().from('kids_learning_paths').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsLearningPath[];
}

export async function saveLearningPath(input: KidsLearningPathInput): Promise<KidsLearningPath> {
  const { data, error } = await getSupabase()
    .from('kids_learning_paths')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsLearningPath;
}

export async function listAdminLearningModules(): Promise<KidsLearningModule[]> {
  const { data, error } = await getSupabase().from('kids_learning_modules').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsLearningModule[];
}

export async function saveLearningModule(input: KidsLearningModuleInput): Promise<KidsLearningModule> {
  const { data, error } = await getSupabase()
    .from('kids_learning_modules')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsLearningModule;
}

export async function listAdminLessons(): Promise<KidsLesson[]> {
  const { data, error } = await getSupabase().from('kids_lessons').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsLesson[];
}

export async function saveLesson(input: KidsLessonInput): Promise<KidsLesson> {
  const { data, error } = await getSupabase()
    .from('kids_lessons')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsLesson;
}

export async function listAdminAvatars(): Promise<KidsAvatar[]> {
  const { data, error } = await getSupabase().from('kids_avatars').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsAvatar[];
}

export async function saveAvatar(input: KidsAvatarInput): Promise<KidsAvatar> {
  const { data, error } = await getSupabase()
    .from('kids_avatars')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsAvatar;
}

export async function listAdminRewardItems(): Promise<KidsRewardItem[]> {
  const { data, error } = await getSupabase().from('kids_reward_items').select('*').order('cost_stars');
  throwIfError(error);
  return (data ?? []) as KidsRewardItem[];
}

export async function saveRewardItem(input: KidsRewardItemInput): Promise<KidsRewardItem> {
  const { data, error } = await getSupabase()
    .from('kids_reward_items')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsRewardItem;
}

export async function listAdminFeatureFlags(): Promise<KidsFeatureFlag[]> {
  const { data, error } = await getSupabase().from('kids_feature_flags').select('*').order('launch_order');
  throwIfError(error);
  return (data ?? []) as KidsFeatureFlag[];
}

export async function saveFeatureFlag(input: KidsFeatureFlagInput): Promise<KidsFeatureFlag> {
  const { data, error } = await getSupabase()
    .from('kids_feature_flags')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsFeatureFlag;
}

export async function listAdminBadges(): Promise<KidsBadge[]> {
  const { data, error } = await getSupabase().from('kids_badges').select('*').order('sort_order');
  throwIfError(error);
  return (data ?? []) as KidsBadge[];
}

export async function saveBadge(input: KidsBadgeInput): Promise<KidsBadge> {
  const { data, error } = await getSupabase()
    .from('kids_badges')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsBadge;
}

export async function isCurrentUserKidsAdmin(): Promise<boolean> {
  const { data, error } = await getSupabase().rpc('is_kids_admin');
  throwIfError(error);
  return Boolean(data);
}

export async function listAdminQuizzes(): Promise<KidsQuiz[]> {
  const { data, error } = await getSupabase().from('kids_quizzes').select('*').order('updated_at', { ascending: false });
  throwIfError(error);
  return (data ?? []) as KidsQuiz[];
}

export async function listAdminQuizReadiness(): Promise<AdminQuizReadinessRow[]> {
  const client = getSupabase();
  const [quizzesResult, categoriesResult, questionsResult, optionsResult] = await Promise.all([
    client.from('kids_quizzes').select('*').order('updated_at', { ascending: false }),
    client.from('kids_quiz_categories').select('*'),
    client.from('kids_quiz_questions').select('*'),
    client.from('kids_quiz_options').select('*'),
  ]);

  [quizzesResult, categoriesResult, questionsResult, optionsResult].forEach((result) => throwIfError(result.error));

  const categories = (categoriesResult.data ?? []) as KidsQuizCategory[];
  const questions = (questionsResult.data ?? []) as KidsQuizQuestion[];
  const options = (optionsResult.data ?? []) as KidsQuizOption[];
  const categoryById = new Map(categories.map((category) => [category.id, category.title]));
  const optionsByQuestionId = new Map<string, KidsQuizOption[]>();

  options.forEach((option) => {
    const current = optionsByQuestionId.get(option.question_id) ?? [];
    current.push(option);
    optionsByQuestionId.set(option.question_id, current);
  });

  return ((quizzesResult.data ?? []) as KidsQuiz[]).map((quiz) => {
    const quizQuestions = questions.filter((question) => question.quiz_id === quiz.id && question.is_active !== false);
    const invalidAnswerQuestions = quizQuestions.filter((question) => {
      const questionOptions = optionsByQuestionId.get(question.id) ?? [];
      return questionOptions.length !== 4 || questionOptions.filter((option) => option.is_correct).length !== 1;
    }).length;
    const missingExplanations = quizQuestions.filter((question) => (question.explanation ?? '').trim().length < 20).length;
    const missingQuestions = Math.max((quiz.question_count ?? 0) - quizQuestions.length, 0);
    const status: AdminQuizReadinessRow['status'] = missingQuestions
      ? 'Needs questions'
      : invalidAnswerQuestions
        ? 'Fix answers'
        : missingExplanations
          ? 'Add explanations'
          : 'Ready to publish';

    return {
      quiz_id: quiz.id,
      quiz_title: quiz.title,
      quiz_slug: quiz.slug,
      publish_status: quiz.status ?? 'draft',
      is_active: quiz.is_active ?? true,
      category_title: categoryById.get(quiz.category_id) ?? 'Uncategorized',
      declared_questions: quiz.question_count ?? 0,
      actual_questions: quizQuestions.length,
      missing_questions: missingQuestions,
      invalid_answer_questions: invalidAnswerQuestions,
      missing_explanations: missingExplanations,
      status,
    };
  });
}

export async function publishAdminQuiz(quizId: string) {
  const { data, error } = await getSupabase().rpc('publish_kids_quiz', {
    p_quiz_id: quizId,
  });

  throwIfError(error);
  return data as {
    quiz_id: string;
    quiz_slug: string;
    quiz_title: string;
    status: 'published';
    question_count: number;
  };
}

export async function archiveAdminQuiz(quizId: string) {
  const { data, error } = await getSupabase().rpc('archive_kids_quiz', {
    p_quiz_id: quizId,
  });

  throwIfError(error);
  return data as {
    quiz_id: string;
    quiz_slug: string;
    quiz_title: string;
    status: 'archived';
    is_active: boolean;
  };
}

export async function saveQuizDraft(input: KidsQuizDraftInput) {
  const { data, error } = await getSupabase()
    .from('kids_quizzes')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsQuiz;
}

export async function saveQuizQuestionDraft(input: KidsQuizQuestionDraftInput): Promise<QuizWithQuestions['questions'][number]> {
  const { data: question, error: questionError } = await getSupabase()
    .from('kids_quiz_questions')
    .upsert(
      {
        quiz_id: input.quiz_id,
        prompt: input.prompt,
        arabic_text: input.arabic_text,
        hint: input.hint,
        explanation: input.explanation,
        fun_fact: input.fun_fact,
        question_type: 'multiple_choice',
        sort_order: input.sort_order,
        is_active: input.is_active,
      },
      { onConflict: 'quiz_id,sort_order' },
    )
    .select('*')
    .single();

  throwIfError(questionError);

  const questionId = (question as KidsQuizQuestion).id;
  const { data: options, error: optionsError } = await getSupabase()
    .from('kids_quiz_options')
    .upsert(
      input.options.map((option) => ({
        question_id: questionId,
        option_label: option.option_label,
        option_text: option.option_text,
        is_correct: option.is_correct,
        feedback_text: option.feedback_text,
        sort_order: option.sort_order,
      })),
      { onConflict: 'question_id,option_label' },
    )
    .select('*')
    .order('sort_order');

  throwIfError(optionsError);
  return {
    ...(question as KidsQuizQuestion),
    options: (options ?? []) as KidsQuizOption[],
  };
}

export async function listAdminArticles(): Promise<KidsLearningArticle[]> {
  const { data, error } = await getSupabase().from('kids_learning_articles').select('*').order('updated_at', { ascending: false });
  throwIfError(error);
  return (data ?? []) as KidsLearningArticle[];
}

export async function listAdminQuizReports(): Promise<AdminQuizReportRow[]> {
  const { data, error } = await getSupabase()
    .from('kids_quiz_attempts')
    .select('quiz_id, status, correct_count, total_questions, stars_earned, xp_earned, quiz:kids_quizzes(title, slug)')
    .eq('status', 'completed')
    .order('completed_at', { ascending: false, nullsFirst: false });

  throwIfError(error);

  const byQuiz = new Map<string, {
    quiz_id: string;
    quiz_title: string;
    quiz_slug: string;
    attempts: number;
    correct: number;
    questions: number;
    total_stars: number;
    total_xp: number;
  }>();

  (data ?? []).forEach((attempt: unknown) => {
    const row = attempt as {
      quiz_id: string;
      correct_count: number;
      total_questions: number;
      stars_earned: number;
      xp_earned: number;
      quiz?: { title?: string; slug?: string } | { title?: string; slug?: string }[] | null;
    };
    const quiz = Array.isArray(row.quiz) ? row.quiz[0] : row.quiz;
    const current = byQuiz.get(row.quiz_id) ?? {
      quiz_id: row.quiz_id,
      quiz_title: quiz?.title ?? 'Quran Quiz',
      quiz_slug: quiz?.slug ?? row.quiz_id,
      attempts: 0,
      correct: 0,
      questions: 0,
      total_stars: 0,
      total_xp: 0,
    };

    current.attempts += 1;
    current.correct += row.correct_count;
    current.questions += row.total_questions;
    current.total_stars += row.stars_earned;
    current.total_xp += row.xp_earned;
    byQuiz.set(row.quiz_id, current);
  });

  return Array.from(byQuiz.values()).map((row) => {
    const average_accuracy = row.questions ? Math.round((row.correct / row.questions) * 100) : 0;
    return {
      quiz_id: row.quiz_id,
      quiz_title: row.quiz_title,
      quiz_slug: row.quiz_slug,
      attempts: row.attempts,
      average_accuracy,
      total_stars: row.total_stars,
      total_xp: row.total_xp,
      status: row.attempts === 0 ? 'Needs attempts' : average_accuracy < 70 ? 'Review explanations' : 'Healthy',
    };
  });
}

export async function listAdminParentRequests(): Promise<KidsParentRequest[]> {
  const { data, error } = await getSupabase()
    .from('kids_parent_requests')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  throwIfError(error);
  return (data ?? []) as KidsParentRequest[];
}

export async function updateAdminParentRequest(input: {
  id: string;
  status: KidsParentRequest['status'];
  reviewer_notes: string | null;
}): Promise<KidsParentRequest> {
  const { data, error } = await getSupabase()
    .from('kids_parent_requests')
    .update({
      status: input.status,
      reviewer_notes: input.reviewer_notes,
    })
    .eq('id', input.id)
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsParentRequest;
}

export async function listAdminAuditLog(): Promise<KidsCmsAuditLog[]> {
  const { data, error } = await getSupabase()
    .from('kids_cms_audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(100);

  throwIfError(error);
  return (data ?? []) as KidsCmsAuditLog[];
}

export async function saveLearningArticleDraft(input: KidsLearningArticleDraftInput) {
  const { data, error } = await getSupabase()
    .from('kids_learning_articles')
    .upsert(input, { onConflict: 'slug' })
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsLearningArticle;
}

export async function saveMediaMetadata(input: KidsMediaMetadataInput) {
  const {
    data: { user },
    error: userError,
  } = await getSupabase().auth.getUser();
  throwIfError(userError);

  const { data, error } = await getSupabase()
    .from('kids_media_assets')
    .upsert(
      {
        ...input,
        bucket: 'kids-media',
        created_by: user?.id ?? null,
        public_url: input.public_url ?? null,
        size_bytes: input.size_bytes ?? null,
        status: input.status ?? 'active',
      },
      { onConflict: 'path' },
    )
    .select('*')
    .single();

  throwIfError(error);
  return data as KidsMediaAsset;
}

export async function uploadMediaAsset(input: {
  file: File;
  path: string;
  alt_text: string;
  description?: string | null;
  category: KidsMediaAsset['category'];
  tags?: string[];
}): Promise<KidsMediaAsset> {
  const client = getSupabase();
  const { data: uploadData, error: uploadError } = await client.storage
    .from('kids-media')
    .upload(input.path, input.file, {
      cacheControl: '3600',
      contentType: input.file.type,
      upsert: true,
    });

  throwIfError(uploadError);
  if (!uploadData) {
    throw new Error('Upload did not return a storage path.');
  }

  const { data: publicUrlData } = client.storage.from('kids-media').getPublicUrl(uploadData.path);

  return saveMediaMetadata({
    filename: input.file.name,
    path: uploadData.path,
    mime_type: input.file.type,
    size_bytes: input.file.size,
    public_url: publicUrlData.publicUrl,
    alt_text: input.alt_text,
    description: input.description ?? null,
    category: input.category,
    tags: input.tags ?? [],
    status: 'active',
  });
}
